import { useState, useEffect } from "react";
import api, { formatApiError, applyBrand } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Palette, Clock, Loader2, Monitor, Copy, BellRing, MapPin } from "lucide-react";

const PRESETS = ["#003366", "#FF8C00", "#7C3AED", "#10B981", "#F59E0B", "#EF4444", "#0F172A", "#2563EB"];

export default function Branding() {
  const { user, refresh } = useAuth();
  const [color, setColor] = useState("#003366");
  const [logo, setLogo] = useState("");
  const [saving, setSaving] = useState(false);
  const [notifyId, setNotifyId] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [savingNotify, setSavingNotify] = useState(false);
  const [geoMode, setGeoMode] = useState("optional");
  const [savingGeo, setSavingGeo] = useState(false);
  const slug = user?.company?.slug;
  const kioskUrl = `${window.location.origin}/kiosk/${slug}`;

  useEffect(() => {
    api.get("/company/me").then(({ data }) => { setColor(data.primary_color || "#003366"); setLogo(data.logo_url || ""); });
    api.get("/company/notify-config").then(({ data }) => { setNotifyId(data.notify_user_id || ""); setCandidates(data.candidates || []); });
    api.get("/company/settings").then(({ data }) => setGeoMode(data.geo_mode || "optional"));
  }, []);

  const preview = (c) => { setColor(c); applyBrand(c); };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/company/branding", { primary_color: color, logo_url: logo });
      applyBrand(color);
      await refresh();
      toast.success("Marca blanca actualizada");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveNotify = async (val) => {
    setNotifyId(val);
    setSavingNotify(true);
    try {
      await api.patch("/company/notify-config", { notify_user_id: val });
      toast.success("Persona responsable actualizada");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingNotify(false); }
  };

  const saveGeo = async (val) => {
    setGeoMode(val);
    setSavingGeo(true);
    try {
      await api.patch("/company/settings", { geo_mode: val });
      toast.success("Geolocalización del fichaje actualizada");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingGeo(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Marca Blanca</h1>
        <p className="text-slate-500 text-sm mt-1">Personaliza la identidad visual de tu empresa.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-slate-200 shadow-none space-y-5">
          <div className="flex items-center gap-2"><Palette className="h-4 w-4 text-slate-400" /><h3 className="font-heading font-semibold">Identidad visual</h3></div>
          <div>
            <Label>Color corporativo</Label>
            <div className="flex items-center gap-3 mt-2">
              <input type="color" value={color} onChange={(e) => preview(e.target.value)} data-testid="color-picker" className="h-11 w-14 rounded-lg border border-slate-200 cursor-pointer" />
              <Input value={color} onChange={(e) => preview(e.target.value)} className="h-11 w-32 font-mono" data-testid="color-hex" />
            </div>
            <div className="flex gap-2 mt-3">
              {PRESETS.map((p) => (
                <button key={p} onClick={() => preview(p)} className="h-8 w-8 rounded-full border-2 transition-transform hover:scale-110" style={{ background: p, borderColor: color === p ? "#0F172A" : "transparent" }} data-testid={`preset-${p}`} />
              ))}
            </div>
          </div>
          <div>
            <Label>URL del logotipo</Label>
            <Input value={logo} onChange={(e) => setLogo(e.target.value)} placeholder="https://..." className="mt-1.5 h-11" data-testid="logo-url" />
            <p className="text-xs text-slate-400 mt-1">Pega la URL de tu logo (PNG/JPG).</p>
          </div>
          <Button onClick={save} disabled={saving} className="brand-btn w-full h-11" data-testid="save-branding-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar cambios"}
          </Button>
        </Card>

        <div className="space-y-6">
          <Card className="p-6 border-slate-200 shadow-none">
            <h3 className="font-heading font-semibold mb-4">Vista previa</h3>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="h-14 flex items-center gap-3 px-4" style={{ background: color }}>
                {logo ? <img src={logo} alt="logo" className="h-8 w-8 rounded-md object-cover bg-white/20" /> : <div className="h-8 w-8 rounded-md bg-white/20 flex items-center justify-center"><Clock className="h-5 w-5 text-white" /></div>}
                <span className="font-heading font-bold text-white">{user?.company?.name}</span>
              </div>
              <div className="p-4 space-y-3">
                <div className="h-3 w-2/3 rounded bg-slate-100" />
                <div className="h-3 w-1/2 rounded bg-slate-100" />
                <Button className="brand-btn" style={{ background: color }} size="sm">Botón de acción</Button>
              </div>
            </div>
          </Card>

          <Card className="p-6 border-slate-200 shadow-none">
            <div className="flex items-center gap-2 mb-2"><BellRing className="h-4 w-4 text-slate-400" /><h3 className="font-heading font-semibold">Persona responsable de avisos</h3></div>
            <p className="text-sm text-slate-500 mb-3">Recibirá los recordatorios automáticos: caducidad de documentos (formación, EPIs, salud) y el escalado de turnos sin cerrar.</p>
            <Select value={notifyId} onValueChange={saveNotify} disabled={savingNotify}>
              <SelectTrigger data-testid="notify-user-select"><SelectValue placeholder="Selecciona responsable" /></SelectTrigger>
              <SelectContent>
                {candidates.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`notify-opt-${c.id}`}>{c.name}{c.email ? ` · ${c.email}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </Card>

          <Card className="p-6 border-slate-200 shadow-none">
            <div className="flex items-center gap-2 mb-2"><MapPin className="h-4 w-4 text-slate-400" /><h3 className="font-heading font-semibold">Geolocalización del fichaje</h3></div>
            <p className="text-sm text-slate-500 mb-3">Registra la ubicación GPS del empleado al fichar entrada y salida (fichaje web/móvil).</p>
            <Select value={geoMode} onValueChange={saveGeo} disabled={savingGeo}>
              <SelectTrigger data-testid="geo-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="off" data-testid="geo-opt-off">Desactivada</SelectItem>
                <SelectItem value="optional" data-testid="geo-opt-optional">Opcional · se guarda si el navegador la facilita</SelectItem>
                <SelectItem value="required" data-testid="geo-opt-required">Obligatoria · sin ubicación no se puede fichar</SelectItem>
              </SelectContent>
            </Select>
          </Card>

          <Card className="p-6 border-slate-200 shadow-none">
            <div className="flex items-center gap-2 mb-3"><Monitor className="h-4 w-4 text-slate-400" /><h3 className="font-heading font-semibold">Enlace del Quiosco</h3></div>
            <p className="text-sm text-slate-500 mb-3">Abre este enlace en una tablet en la entrada para el fichaje físico.</p>
            <div className="flex gap-2">
              <Input readOnly value={kioskUrl} className="h-10 text-sm" data-testid="kiosk-url" />
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(kioskUrl); toast.success("Enlace copiado"); }} data-testid="copy-kiosk-btn"><Copy className="h-4 w-4" /></Button>
            </div>
            <a href={kioskUrl} target="_blank" rel="noreferrer"><Button variant="ghost" className="mt-2 text-sky-600" data-testid="open-kiosk-btn"><Monitor className="h-4 w-4 mr-2" /> Abrir quiosco</Button></a>
          </Card>
        </div>
      </div>
    </div>
  );
}
